// Atlas Overpass fetch + parse (ATL-1). Client-side (network lives here; the
// parser is DOM-free so it unit-tests against the committed fixture). Converts a
// raw Overpass JSON response into the pure intermediate city model, projecting
// node lat/lon to local ENU block coordinates around the bbox centre.
import {
  intermediateCitySchema, AEROWAY_KINDS,
  type IntermediateCity, type Polyline, type Polygon, type Building, type RoadClass, type Aeroway, type AerowayKind,
} from '../../sim/atlas/intermediate';

export interface LatLon { lat: number; lon: number; }

type XZ = [number, number];

interface OsmNode { type: 'node'; id: number; lat: number; lon: number; tags?: Record<string, string>; }
interface OsmWay { type: 'way'; id: number; nodes: number[]; tags?: Record<string, string>; }
interface OsmRelMember { type: string; ref: number; role: string; }
interface OsmRelation { type: 'relation'; id: number; members: OsmRelMember[]; tags?: Record<string, string>; }
type OsmElement = OsmNode | OsmWay | OsmRelation;
export interface OverpassJson { elements: OsmElement[]; }

const M_PER_DEG_LAT = 111_320;

/** Project (lat,lon) to local block coords around `center` at `scale` m/block. */
export function projectLatLon(lat: number, lon: number, center: LatLon, scale: number): XZ {
  const mPerDegLon = M_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180);
  const east = (lon - center.lon) * mPerDegLon;
  const north = (lat - center.lat) * M_PER_DEG_LAT;
  // `+ 0` normalizes -0 → 0 so identical coordinates compare structurally equal.
  return [Math.round(east / scale) + 0, Math.round(-north / scale) + 0]; // z grows south
}

/** Map an OSM `highway` value to one of our five road classes (ATL-1). */
export function roadClassOf(highway: string): RoadClass {
  switch (highway) {
    case 'motorway': case 'trunk': case 'motorway_link': case 'trunk_link': return 'motorway';
    case 'primary': case 'primary_link': return 'primary';
    case 'secondary': case 'tertiary': case 'secondary_link': case 'tertiary_link': return 'secondary';
    case 'footway': case 'path': case 'pedestrian': case 'cycleway': case 'steps': return 'footway';
    default: return 'residential';
  }
}

function isGreen(tags: Record<string, string>): boolean {
  return tags.natural === 'wood'
    || ['grass', 'forest', 'meadow', 'recreation_ground'].includes(tags.landuse ?? '')
    || ['park', 'garden', 'pitch'].includes(tags.leisure ?? '');
}

/** Parse a raw Overpass response into the intermediate city model (ATL-1, E-20). */
export function parseOverpass(json: OverpassJson, center: LatLon, scale: number, sizeBlocks: number, label = ''): IntermediateCity {
  const nodes = new Map<number, OsmNode>();
  const ways = new Map<number, OsmWay>();
  for (const el of json.elements) {
    if (el.type === 'node') nodes.set(el.id, el);
    else if (el.type === 'way') ways.set(el.id, el);
  }
  const ringOf = (way: OsmWay): XZ[] => way.nodes.map((n) => { const nd = nodes.get(n)!; return projectLatLon(nd.lat, nd.lon, center, scale); });

  const roads: Polyline[] = [];
  const water: Polygon[] = [];
  const green: Polygon[] = [];
  const buildings: Building[] = [];
  const trees: XZ[] = [];
  const aeroways: Aeroway[] = [];

  for (const el of json.elements) {
    if (el.type === 'node') {
      if (el.tags?.natural === 'tree') trees.push(projectLatLon(el.lat, el.lon, center, scale));
      continue;
    }
    const tags = (el as OsmWay | OsmRelation).tags ?? {};

    if (el.type === 'way') {
      if (tags.highway) { roads.push({ points: ringOf(el).slice(0, Math.max(2, el.nodes.length)), roadClass: roadClassOf(tags.highway) }); continue; }
      if (tags.aeroway && (AEROWAY_KINDS as readonly string[]).includes(tags.aeroway)) {
        const kind = tags.aeroway as AerowayKind;
        const pts = ringOf(el);
        const min = kind === 'runway' || kind === 'taxiway' ? 2 : 3;
        if (pts.length >= min) {
          const w = tags.width ? Number(tags.width) : NaN;
          aeroways.push({ kind, points: pts, ...(Number.isFinite(w) && w > 0 ? { widthM: w } : {}), ...(tags.ref ? { ref: tags.ref } : {}) });
        }
        continue;
      }
      const ring = ringOf(el);
      if (ring.length < 3) continue;
      if (tags.building || tags['building:part']) {
        const levels = Math.max(1, Math.round(Number(tags['building:levels'] ?? '2')) || 2);
        buildings.push({ footprint: ring, levels });
      } else if (tags.natural === 'water' || tags.waterway) {
        water.push({ points: ring });
      } else if (isGreen(tags)) {
        green.push({ points: ring });
      }
      continue;
    }

    // Relations: multipolygons with inner rings become holes (courtyards/lakes, E-20).
    if (el.type === 'relation') {
      const outers = el.members.filter((m) => m.role === 'outer' && ways.has(m.ref)).map((m) => ringOf(ways.get(m.ref)!));
      const holes = el.members.filter((m) => m.role === 'inner' && ways.has(m.ref)).map((m) => ringOf(ways.get(m.ref)!)).filter((r) => r.length >= 3);
      for (const outer of outers) {
        if (outer.length < 3) continue;
        const poly: Polygon = holes.length ? { points: outer, holes } : { points: outer };
        if (tags.building) buildings.push({ footprint: outer, levels: Math.max(1, Math.round(Number(tags['building:levels'] ?? '2')) || 2), holes: holes.length ? holes : undefined });
        else if (tags.natural === 'water') water.push(poly);
        else if (isGreen(tags)) green.push(poly);
      }
    }
  }

  return intermediateCitySchema.parse({ version: 1, scale, sizeBlocks, simulated: false, label, roads, water, green, buildings, trees, aeroways });
}

/** The Overpass QL query for a bbox (ATL-1). */
export function overpassQuery(south: number, west: number, north: number, east: number): string {
  const bbox = `${south},${west},${north},${east}`;
  return `[out:json][timeout:25];(` +
    `way["highway"](${bbox});` +
    `way["building"](${bbox});rel["building"](${bbox});` +
    `way["natural"~"wood|water"](${bbox});rel["natural"="water"](${bbox});` +
    `way["landuse"~"grass|forest|meadow|recreation_ground"](${bbox});` +
    `way["leisure"~"park|garden|pitch"](${bbox});` +
    `way["aeroway"~"runway|taxiway|apron|helipad"](${bbox});` +
    `);out body geom;>;out skel qt;`;
}

/** Fetch Overpass for a bbox with a 10 s timeout (ATL-1); throws on any failure. */
export async function fetchOverpass(south: number, west: number, north: number, east: number, signal?: AbortSignal): Promise<OverpassJson> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 10_000);
  signal?.addEventListener('abort', () => ctrl.abort());
  try {
    const res = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain', 'User-Agent': 'TerraForge/1.0 (education; contact via github)' },
      body: overpassQuery(south, west, north, east),
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`overpass HTTP ${res.status}`);
    return (await res.json()) as OverpassJson;
  } finally {
    clearTimeout(timer);
  }
}
